import { useEffect } from "react";
import { Box, Typography, IconButton, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  AddEmissionContext,
  EmissionMode,
  FormValuesStepOne,
  FormValuesStepTwo,
  getEvidenceFileInfo,
  EvidenceInfo,
} from "../../../../contexts/AddEmissionContext";

import AddEmissionStepTwo from "./step2/AddEmissionStepTwo";
import { FormProvider } from "react-hook-form";
import alertService from "../../../../components/alert/alertService";
import ActivitiesService from "../../../../service/activities.service";
import AddEmissionStepOne from "./step1/AddEmissionStepOne";
import { GetActivities } from "../../../../models/Activities";
import CustomModal from "../../../../components/modal/CustomModal";
import dayjs, { Dayjs } from "dayjs";
import { DataEntryRoleContext } from "../../../../contexts/DataEntryRoleContext";
import { common, input } from "../../../../i18n";
import { OrganizationBreadcrumbs } from "../OrganizationBreadcrumbs";

const AddEmissionForm = () => {
  const { t } = useTranslation([common, input], {
    nsMode: "fallback",
  });

  const {
    step,
    setStep,
    open,
    handleOpen,
    handleClose,
    stepOneForm,
    stepTwoForm,
    prepareFormDataToCreateOrDraftActivities,
    setUpActivitiesListData,
    setEmissionMode,
    subOrganizationList,
    year,
    setYear,
  } = AddEmissionContext();

  const { updateActivitiesAfterEdit } = DataEntryRoleContext();

  const closeModal = () => {
    stepOneForm.reset();
    stepTwoForm.reset();
    handleClose();
  };

  const stepOneOnSubmit = (data: FormValuesStepOne) => {
    setStep(step + 1);
  };

  const stepTwoOnSubmit = async (data: FormValuesStepTwo, event: any) => {
    const subOrgRefId =
      subOrganizationList.find(
        (s) => s.id === stepOneForm.getValues("subOrganizations"),
      )?.refId ?? "";
    let evidence: EvidenceInfo;
    try {
      evidence = await getEvidenceFileInfo(
        stepTwoForm.getValues("attachmentRef"),
        stepTwoForm.getValues("attachment"),
        stepTwoForm.getValues("startDate").year(),
        subOrgRefId,
      );
    } catch (error) {
      // not every throw here is alerted: a corrupt attachment string fails in base64 decoding
      console.error("Evidence resolution failed before save", error);
      return;
    }
    let createActivitiesResult: GetActivities | undefined;
    try {
      createActivitiesResult = await ActivitiesService.create(
        prepareFormDataToCreateOrDraftActivities(evidence),
      );
    } catch {
      // error alert already shown by service
      return;
    }

    if (createActivitiesResult) {
      setUpActivitiesListData(createActivitiesResult);

      let updateYear: Dayjs;

      try {
        updateYear = dayjs(createActivitiesResult.activities[0]?.startDate);
      } catch (error) {
        updateYear = year;
      }

      if (year.year() !== updateYear.year()) {
        setYear(updateYear);
      }

      closeModal();

      await updateActivitiesAfterEdit();

      alertService.mixin(t("emission-created-successfully"));
    }
  };

  const onError = (errors: any) => {
    const inputError: string[] = Object.keys(errors);

    if (inputError.length > 0) {
      const form: HTMLElement | null = document.getElementById(inputError[0]);

      if (form)
        form.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
    }
  };

  useEffect(() => {
    setEmissionMode(EmissionMode.AddEmission);
  }, []);

  return (
    <>
      <Button
        fullWidth
        variant="contained"
        color="secondary"
        startIcon={<img src="/img/add-circle.svg" alt="add-circle" />}
        onClick={handleOpen}
      >
        {t("Add-Emission")}
      </Button>

      <CustomModal
        header={
          <>
            <Typography variant="h6">
              {step === 2 && (
                <IconButton
                  sx={{
                    marginLeft: "-8px",
                  }}
                  onClick={() => {
                    stepTwoForm.reset();
                    setStep(step - 1);
                  }}
                >
                  <img src="/img/arrow-left.svg" alt="" />
                </IconButton>
              )}
              {t("Add-Emission")}({step}/2)
            </Typography>
            {step === 2 ? (
              <OrganizationBreadcrumbs
                organization={stepOneForm.getValues("organization") ?? ""}
                location={stepOneForm.getValues("location") ?? ""}
                subOrganizations={
                  subOrganizationList.find(
                    (i) => i.id === stepOneForm.getValues("subOrganizations"),
                  )?.name ?? ""
                }
                site={subOrganizationList
                  .find(
                    (i) => i.id === stepOneForm.getValues("subOrganizations"),
                  )
                  ?.sites.find(
                    (site) => site.refId === stepOneForm.getValues("site"),
                  )}
              />
            ) : null}
          </>
        }
        body={
          <>
            {step === 1 ? (
              <FormProvider {...stepOneForm}>
                <form
                  onSubmit={stepOneForm.handleSubmit(stepOneOnSubmit, onError)}
                >
                  <Box>
                    <AddEmissionStepOne />
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                  >
                    <Button
                      fullWidth
                      color="secondary"
                      variant="contained"
                      type="submit"
                    >
                      {t("Next")}
                    </Button>
                  </Box>
                </form>
              </FormProvider>
            ) : (
              <FormProvider {...stepTwoForm}>
                <form
                  onSubmit={stepTwoForm.handleSubmit(stepTwoOnSubmit, onError)}
                >
                  <AddEmissionStepTwo />

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      paddingTop: "44px",
                    }}
                    rowGap={2.5}
                  >
                    <Button
                      fullWidth
                      color="secondary"
                      variant="contained"
                      type="submit"
                      name="add"
                      disabled={
                        stepTwoForm.formState.isLoading &&
                        stepTwoForm.formState.isSubmitting
                      }
                    >
                      {t("Add")}
                    </Button>
                  </Box>
                </form>
              </FormProvider>
            )}
          </>
        }
        open={open}
        onClose={closeModal}
      />
    </>
  );
};

export default AddEmissionForm;
